import { AxiosError, AxiosInstance } from 'axios';
import { EXPORTED_FILE_DOWNLOAD, MAINTENANCE, STATUS } from '../ui/constants/urls';

/** Status code the backend returns while it is in a maintenance window. */
export const MAINTENANCE_STATUS_CODE = 491;

/** Pages that must keep working while the backend is in maintenance mode. */
const ALLOWED_PATHNAMES = [MAINTENANCE, STATUS, EXPORTED_FILE_DOWNLOAD];

let redirecting = false;

export const isMaintenanceResponse = (status?: number): boolean =>
  status === MAINTENANCE_STATUS_CODE;

/**
 * True once a maintenance redirect has been started. Anything that navigates
 * the browser on its own (the SSO login redirect, for example) has to bail out
 * while this is set, otherwise it replaces the pending maintenance navigation.
 */
export const isMaintenanceRedirecting = (): boolean => redirecting;

/**
 * Sends the browser to the maintenance page. Every 491 goes through here, so
 * the navigation happens exactly once and cannot be undone by a redirect that
 * is queued afterwards.
 */
export const handleMaintenance = (): void => {
  if (redirecting || ALLOWED_PATHNAMES.includes(window.location.pathname)) {
    return;
  }

  redirecting = true;
  window.location.replace(MAINTENANCE);
};

const instrumentedClients = new WeakSet<AxiosInstance>();

/**
 * Redirects to the maintenance page whenever `client` receives a 491, so the
 * behaviour does not depend on the request going through the redux middleware.
 */
export const registerMaintenanceInterceptor = (client: AxiosInstance): void => {
  if (instrumentedClients.has(client)) {
    return;
  }

  instrumentedClients.add(client);

  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (isMaintenanceResponse(error?.response?.status)) {
      handleMaintenance();
    }

    return await Promise.reject(error);
  });
};
